/**
 * BusBookingConfirmation.jsx
 *
 * Success confirmation card shown after a bus ticket is issued.
 * Mirrors BookingConfirmation.js structure/style, adapted for bus data shape.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// ── Helpers ───────────────────────────────────────────────────────────────────
const safeParse = (d) => {
  if (!d) return {};
  if (typeof d === "object") return d;
  try {
    const p = JSON.parse(d);
    return typeof p === "string" ? JSON.parse(p) : p;
  } catch {
    return {};
  }
};

const fmtTime = (timeStr) =>
  timeStr?.replace(/:00 /, " ").trim() || "--";

const fmtDate = (dateStr) => {
  if (!dateStr) return "--";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const cleanPointName = (name) =>
  name?.replace(/^cityflo:\s+/, "").trim() || name || "--";

// ── Component ─────────────────────────────────────────────────────────────────
const BusBookingConfirmation = ({ bookingData, onContactSupport }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const ticket   = safeParse(bookingData?.ticket_details);
  const rcId     = bookingData?.rc_booking_id || "--";
  const etsNum   = bookingData?.ets_ticket_number || ticket?.etstnumber || "--";
  const opPnr    = bookingData?.op_pnr            || ticket?.opPNR      || "--";
  // block_payload: parsed by page or extracted from booking_request
  const blockPayload = bookingData?.block_payload ||
    (() => {
      const br = safeParse(bookingData?.booking_request);
      return safeParse(br?.block_payload) || br || {};
    })();

  // segment_info: rich bus data stored at order creation (travel_name, bus_type, times)
  const segmentInfo = bookingData?.segment_info
    ? safeParse(bookingData.segment_info)
    : (() => {
        const br = safeParse(bookingData?.booking_request);
        return br?.segment_info || {};
      })();
  // segment_info stores full ResultCard busInfo — actual segment data is nested under .segment
  const seg = segmentInfo?.segment || segmentInfo || {};

  const operator = bookingData?.travel_name || bookingData?.service_name
                || seg?.travel_name || seg?.service_name || seg?.flight_name || "--";
  const busType  = bookingData?.bus_type || ticket?.service_type
                || seg?.bus_type || seg?.seat_class_ui || "--";

  // Journey info from ticket or booking
  const sourceCity = blockPayload?.sourceCity || ticket?.sourceCity || bookingData?.source_name || seg?.origin_name || "--";
  const destCity   = blockPayload?.destinationCity || ticket?.destinationCity || bookingData?.destination_name || seg?.dest_name || "--";
  const journeyDate = fmtDate(
    ticket?.journeyDate || bookingData?.journey_date
  );
  const depTimeFmt = seg?.origin_dep_dt
    ? new Date(seg.origin_dep_dt).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})
    : null;
  const depTime    = depTimeFmt || fmtTime(ticket?.departureTime || blockPayload?.boardingPoint?.time || "--");

  // Points
  // boarding/dropping: prefer block_payload (has selected point), fall back to DDB field
  const boardingRaw = blockPayload?.boardingPoint
    || safeParse(bookingData?.boarding_point)
    || {};
  const droppingRaw = blockPayload?.droppingPoint
    || safeParse(bookingData?.dropping_point)
    || {};
  const boardingName = cleanPointName(
    boardingRaw?.location || boardingRaw?.name || boardingRaw?.pointName || ticket?.boardingPoint
  );
  const droppingName = cleanPointName(
    droppingRaw?.location || droppingRaw?.name || droppingRaw?.pointName || ticket?.droppingPoint
  );
  const boardingTime = fmtTime(boardingRaw?.time);
  const droppingTime = fmtTime(droppingRaw?.time);

  // Fare
  // ETS returns totalFare: 0 for some operators — use booking-level amount as primary
  const totalFare = parseFloat(
      bookingData?.final_total_with_discounts ||
      bookingData?.amount                     ||
      bookingData?.total_fare                 ||
      ticket?.totalFareWithTaxes              ||
      ticket?.totalFare                       ||
      0
  ) || null;





  const arrTime = seg?.dest_arr_dt
    ? new Date(seg.dest_arr_dt).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})
    : (blockPayload?.droppingPoint?.time || null);
  const paxFromBlock    = blockPayload?.blockSeatPaxDetails || [];
  const baseFareFromPax = paxFromBlock.reduce((s, p) => s + (parseFloat(p.fare) || 0), 0);
  const gstFromPax      = paxFromBlock.reduce((s, p) => s + (parseFloat(p.serviceTaxAmount) || 0), 0);
  const busFareWithTax  = baseFareFromPax + gstFromPax;  // what user actually paid for seat

  const promoDiscount = parseFloat(bookingData?.applied_promo_discount || 0);
  const platformFee   = parseFloat(bookingData?.rc_platform_fee || 0);
  const gstRate       = parseFloat(bookingData?.rc_gst_rate || 18);
  const effectiveFee  = Math.max(0, platformFee - promoDiscount);
  const gstOnFee      = platformFee > 0 ? parseFloat((effectiveFee * gstRate / 100).toFixed(2)) : 0;

  const fmtFare = (v) =>
    v != null && !isNaN(v) && v !== ""
      ? `₹${parseFloat(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
      : "--";

  // Seats
  const seats = bookingData?.seat_numbers || [];
  // ETS ticket has no travelerDetails — use passengers from block_payload
  const travellers = ticket?.travelerDetails?.length
    ? ticket.travelerDetails
    : paxFromBlock;

  // Cancellation policy
  let cancelPolicy = [];
  try {
    const raw = bookingData?.cancellation_policy || ticket?.cancellationPolicy;
    if (raw) cancelPolicy = typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch {}

  const handleCopyId = () => {
    navigator.clipboard.writeText(rcId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleShare = () => {
    const text = `My bus booking is confirmed!\nRoute: ${sourceCity} → ${destCity}\nDate: ${journeyDate}\nPNR: ${opPnr}\nBooking ID: ${rcId}`;
    if (navigator.share) {
      navigator.share({ title: "Bus Booking Confirmed", text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
    }
  };

  return (
    <div className="space-y-4">

      {/* ── Hero banner ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {/* Green success strip */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-5 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-white leading-tight">Bus Ticket Confirmed!</h1>
            <p className="text-emerald-100 text-sm mt-0.5">
              Your seats are reserved. Have a safe journey!
            </p>
          </div>
        </div>

        {/* Booking IDs row */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-6">
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">ETS Ticket No.</p>
            <p className="text-base font-bold text-gray-900 font-mono mt-0.5">{etsNum}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Operator PNR</p>
            <p className="text-base font-bold text-gray-900 font-mono mt-0.5">{opPnr}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">RouteChef Booking ID</p>
            <button
              onClick={handleCopyId}
              className="text-base font-bold text-gray-900 font-mono mt-0.5 flex items-center gap-1.5 hover:text-orange-500 transition-colors"
            >
              {rcId}
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              {copied && <span className="text-xs text-emerald-500 font-normal">Copied!</span>}
            </button>
          </div>
        </div>
      </div>

      {/* ── Main grid ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">

        {/* Left column */}
        <div className="md:col-span-7 space-y-4">

          {/* Journey card */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Journey Details</h2>
              <span className="text-xs bg-orange-50 text-orange-600 font-semibold px-2.5 py-1 rounded-full border border-orange-100">
                {busType}
              </span>
            </div>

            {/* Route timeline */}
            <div className="flex items-start gap-4">
              <div className="flex flex-col items-center pt-1">
                <div className="w-3 h-3 rounded-full bg-orange-500 ring-2 ring-orange-100" />
                <div className="w-0.5 bg-gray-200 flex-1 my-1 min-h-[48px]" />
                <div className="w-3 h-3 rounded-full bg-gray-800 ring-2 ring-gray-100" />
              </div>
              <div className="flex-1 space-y-4">
                <div>
                  <p className="text-lg font-bold text-gray-900">{sourceCity}</p>
                  <p className="text-sm text-gray-500">{boardingName}</p>
                  <p className="text-sm font-semibold text-orange-500 mt-0.5">{boardingTime}</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">{destCity}</p>
                  <p className="text-sm text-gray-500">{droppingName}</p>
                  <p className="text-sm font-semibold text-gray-700 mt-0.5">{droppingTime}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400 font-medium">Journey Date</p>
                <p className="text-sm font-bold text-gray-800 mt-0.5">{journeyDate}</p>
                <p className="text-xs text-gray-400 mt-2">Departure</p>
                <p className="text-sm font-bold text-gray-800">{depTime}</p>
                {arrTime && (<>
                  <p className="text-xs text-gray-400 mt-2">Arrival</p>
                  <p className="text-sm font-bold text-gray-800">{arrTime}</p>
                </>)}
                {seg?.duration_mins > 0 && (
                  <p className="text-xs text-gray-400 mt-2">{Math.floor(seg.duration_mins/60)}h {seg.duration_mins%60}m</p>
                )}
              </div>
            </div>

            {/* Operator */}
            <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20.5V22M7 20.5V22M4 6.78C4 6.14 4 5.82 4.17 5.43c.17-.39.35-.55.71-.87C5.03 3.53 7.36 2 12 2s6.97 1.53 7.12 1.56c.36.32.54.48.71.87.17.39.17.71.17 1.35V14c0 2.83 0 4.24-.88 5.12C18.24 20 16.83 20 14 20H10c-2.83 0-4.24 0-5.12-.88C4 18.24 4 16.83 4 14V6.78z" />
              </svg>
              <span className="text-sm text-gray-600 font-medium">{operator}</span>
            </div>
          </div>

          {/* Passenger / seat table */}
          {(travellers.length > 0 || seats.length > 0) && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
                Passengers &amp; Seats
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 text-gray-400 font-medium">Name</th>
                      <th className="text-center py-2 text-gray-400 font-medium">Seat</th>
                      <th className="text-center py-2 text-gray-400 font-medium">Age</th>
                      <th className="text-center py-2 text-gray-400 font-medium">Gender</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {travellers.length > 0
                      ? travellers.map((t, i) => (
                          <tr key={i}>
                            <td className="py-2.5 font-medium text-gray-800">
                              {(() => {
                                const title = t.title ? t.title + '. ' : '';
                                const n = (t.name || t.firstName || '').trim();
                                const ln = (t.lastName || '').trim();
                                // ETS often sets lastName === name — deduplicate
                                const full = n === ln || !ln || n.toLowerCase().endsWith(ln.toLowerCase())
                                  ? n : `${n} ${ln}`.trim();
                                return (title + full).trim() || '--';
                              })()}
                            </td>
                            <td className="py-2.5 text-center">
                              <span className="inline-block bg-orange-50 text-orange-600 font-bold px-2 py-0.5 rounded text-xs">
                                {t.seatNbr || t.seatNo || "--"}
                              </span>
                            </td>
                            <td className="py-2.5 text-center text-gray-600">{t.age || "--"}</td>
                            <td className="py-2.5 text-center text-gray-600">
                              {t.sex === 'M' || t.gender === 'M' ? 'Male' : t.sex === 'F' || t.gender === 'F' ? 'Female' : t.gender || '--'}
                            </td>
                          </tr>
                        ))
                      : seats.map((s, i) => (
                          <tr key={i}>
                            <td className="py-2.5 font-medium text-gray-500">Passenger {i + 1}</td>
                            <td className="py-2.5 text-center">
                              <span className="inline-block bg-orange-50 text-orange-600 font-bold px-2 py-0.5 rounded text-xs">
                                {s}
                              </span>
                            </td>
                            <td className="py-2.5 text-center text-gray-400">--</td>
                            <td className="py-2.5 text-center text-gray-400">--</td>
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Cancellation policy */}
          {cancelPolicy.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
                Cancellation Policy
              </h2>
              <div className="divide-y divide-gray-50">
                {cancelPolicy.map((p, i) => (
                  <div key={i} className="flex justify-between py-2.5 text-sm">
                    <span className="text-gray-600">{p.cutoffTime}</span>
                    <span className="font-semibold text-gray-800">
                      {p.refundInPercentage}% refund
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column — fare + actions */}
        <div className="md:col-span-5 space-y-4">

          {/* Fare summary */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
              Fare Summary
            </h2>
            <div className="space-y-2.5 text-sm">

              {/* Bus fare — single line, ETS bundles tax in totalFareWithTaxes */}
              <div className="flex justify-between">
                <span className="text-gray-600">Bus fare (incl. taxes)</span>
                <span className="text-gray-800 font-medium">
                  {fmtFare(busFareWithTax || ticket?.actualAmount || (totalFare + promoDiscount))}
                </span>
              </div>

              {/* RC platform fee (0 for buses currently) */}
              {platformFee > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">RouteChef fee</span>
                  <span className="text-gray-800 font-medium">{fmtFare(platformFee)}</span>
                </div>
              )}

              {/* Promo discount */}
              {promoDiscount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Promo ({bookingData?.applied_promo || 'discount'})</span>
                  <span className="font-medium">− {fmtFare(promoDiscount)}</span>
                </div>
              )}

              {/* GST on RC fee — shown after promo */}
              {gstOnFee > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">GST on fee ({gstRate}%)</span>
                  <span className="text-gray-800 font-medium">{fmtFare(gstOnFee)}</span>
                </div>
              )}

              <div className="flex justify-between pt-2 border-t border-gray-100 font-bold">
                <span className="text-gray-800">Total Paid</span>
                <span className="text-gray-900 text-base">{fmtFare(totalFare)}</span>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
              Actions
            </h2>
            <div className="space-y-2">
              <button
                onClick={() => navigate("/booking-history")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-800 hover:bg-gray-800 hover:text-white text-gray-700 text-sm font-medium transition-all group"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Manage Booking
              </button>

              <button
                onClick={handleShare}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-800 hover:bg-gray-800 hover:text-white text-gray-700 text-sm font-medium transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share Booking
              </button>

              <button
                onClick={onContactSupport}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-800 hover:bg-gray-800 hover:text-white text-gray-700 text-sm font-medium transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Contact Support
              </button>

              <button
                onClick={() => navigate("/")}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                Back to Home
              </button>
            </div>
          </div>

          {/* Important info */}
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-sm text-amber-800">
            <p className="font-semibold mb-1">Before you travel</p>
            <ul className="space-y-1 text-amber-700 text-xs">
              <li>• Carry a valid ID proof matching your booking name</li>
              <li>• Arrive at the boarding point 10 minutes before departure</li>
              <li>• Keep your ETS ticket number handy: <strong>{etsNum}</strong></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

BusBookingConfirmation.propTypes = {
  bookingData:      PropTypes.object.isRequired,
  onContactSupport: PropTypes.func,
};

export default BusBookingConfirmation;
